/**
 * The dashboard's old style ratings migration panel (SF-5095): how many customers are on
 * ratings only, on both, or fully moved to surveys; the ratings share of all feedback by month;
 * and the companies still sending the most ratings. SF-5124: the chart sits in the card with a
 * subheading rather than in a card of its own, and the panel splits into two equal columns.
 */
import { Col, Row, Space, Statistic, Table, Typography } from 'antd';
import { TrendChart } from '../../../components/charts/TrendChart';
import { CompanyLink } from '../../../components/usage/CompanyLink';
import type { RatingsMigration } from '../../../components/usage/usageClient';
import { ratingsSharePercent } from '../../../components/usage/usageNumbers';
import { formatNumber, formatPercent } from '../../../utils/format';

const { Text } = Typography;

/** A heading inside a card: bold, so it reads as a heading and not as body text. */
const SubHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text strong style={{ display: 'block', marginBottom: 8 }}>
    {children}
  </Text>
);

type RatingsLeftRow = RatingsMigration['most_ratings_left'][number];

/** One chart point per month that had feedback; a month with none has no share to plot. */
function sharePoints(data: RatingsMigration) {
  return (data.share_by_month ?? []).flatMap((m) => {
    const share = ratingsSharePercent(m.ratings, m.total_feedback);
    return share === null ? [] : [{ bucket: m.period, value: share }];
  });
}

export const RatingsMigrationPanel: React.FC<{ data: RatingsMigration }> = ({ data }) => {
  const counts = [
    { title: 'On ratings only', value: data.counts?.ratings_only },
    { title: 'On ratings and surveys', value: data.counts?.both },
    { title: 'Fully moved to surveys', value: data.counts?.fully_moved },
  ];
  const shareTitle = 'Old style ratings share of all feedback';
  return (
    <Row gutter={[24, 16]}>
      <Col xs={24} lg={12}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Row gutter={[16, 16]}>
            {counts.map((c) => (
              <Col xs={24} sm={8} key={c.title}>
                <Statistic title={c.title} value={formatNumber(c.value)} />
              </Col>
            ))}
          </Row>
          <div>
            <SubHeading>{shareTitle}</SubHeading>
            <TrendChart
              bare
              title={shareTitle}
              data={sharePoints(data)}
              granularity="monthly"
              valueFormatter={(v) => formatPercent(v, 0)}
              height={220}
            />
          </div>
        </Space>
      </Col>
      <Col xs={24} lg={12}>
        <SubHeading>Most old style ratings left</SubHeading>
        <Table<RatingsLeftRow>
          size="small"
          rowKey="company_id"
          pagination={false}
          dataSource={data.most_ratings_left ?? []}
          locale={{ emptyText: 'No customer sent old style ratings this month' }}
          columns={[
            {
              title: 'Company',
              key: 'name',
              render: (_, row) => <CompanyLink companyId={row.company_id} name={row.name} />,
            },
            {
              title: 'Old style ratings',
              key: 'ratings',
              align: 'right',
              render: (_, row) => formatNumber(row.ratings),
            },
          ]}
        />
      </Col>
    </Row>
  );
};
